// Libs
import { AppBar, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { UnderDevelopment } from '~/components/common';
import { DropdownProfile } from '~/components/module/profile';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { StorageEnum } from '~/utils/enum/base';
import { GoogleConnectEnum, RoleEnum } from '~/utils/enum/common';
import {
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_TOOLTIP_DELAY,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { NotificationList } from '~/components/module/notification';
import { notificationActions, selectNotificationUnread } from '~/thunks/notification/notificationSlice';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IQueryParamBase } from '~/utils/interface/common';
import { getListNotification } from '~/thunks/notification/notificationThunk';
import { WebSocketContext, PageHeaderContext } from '~/context';
import { WebSocketEventEnum } from '~/utils/enum/websocket';
// Styles, images, icons
import styles from './Header.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const Header = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const { message: messageWSS } = useContext(WebSocketContext);
  const { title, onBack } = useContext(PageHeaderContext);
  const { profile } = useAppSelector((state) => state.user);
  //#endregion Declare Hook

  //#region Selector
  const notificationUnread: number = useSelector(selectNotificationUnread);
  const { queryParams, isRefreshNotification } = useAppSelector((state) => state.notification);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isOpenNotifications, setIsOpenNotifications] = useState<boolean>(false);

  const [hasMoreNotification, setHasMoreNotification] = useState<boolean>(true);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListNotification(queryParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    if (!isRefreshNotification) return;

    handleGetListNotification(queryParams);
    dispatch(notificationActions.setIsRefreshNotification(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshNotification]);

  useEffect(() => {
    if (!messageWSS) {
      return;
    }

    switch (messageWSS.type) {
      case WebSocketEventEnum.NEW_NOTIFICATION:
        dispatch(notificationActions.setIsRefreshNotification(true));
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageWSS]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleShowNotification = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpenNotifications(true);
  };

  const handleCloseNotifications = () => {
    setAnchorEl(null);
    setIsOpenNotifications(false);
  };

  const handleNavigateMessage = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.message}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.message}`);
        break;
    }
  };

  const handleGetListNotification = (params: IQueryParamBase) => {
    dispatch(getListNotification(params))
      .unwrap()
      .then((res) => {
        const responses = res?.data?.responses;

        if (responses?.length === DEFAULT_NUMBER_ZERO) {
          setHasMoreNotification(false);
        } else {
          setHasMoreNotification(true);
        }
      })
      .catch((error) => {
        setHasMoreNotification(false);
      })
      .finally(() => {});
  };

  const handleLoadMoreNotification = () => {
    dispatch(
      notificationActions.setParamsNotification({
        ...queryParams,
        page: Number(queryParams.page) + DEFAULT_NUMBER_ONE,
      })
    );
  };

  const handleNavigateEmail = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.gmail}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.gmail}`);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <AppBar position='static' color='secondary' className={cx('container')}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        spacing={'20px'}
        className={cx('headerWrap')}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
          {onBack && (
            <IconButton onClick={onBack} size='small' className={cx('headerBackBtn')}>
              <img src={icons.commonIconBack} alt={t('common_alt_img')} className={cx('headerBackIcon')} />
            </IconButton>
          )}
          {title && <Typography className={cx('headerTitle')}>{title}</Typography>}
        </Stack>

        <Stack direction={'row'} spacing={'20px'} justifyContent={'flex-end'} alignItems={'center'}>
          {profile && profile?.google?.status === GoogleConnectEnum.CONNECTED && (
            <Tooltip
              title={t('common_email_tile', { ns: 'common/base' })}
              placement='top'
              enterDelay={DEFAULT_TOOLTIP_DELAY}
              leaveDelay={DEFAULT_TOOLTIP_DELAY}
            >
              <Button
                variant='outlined'
                color='inherit'
                className={cx('headerNotification')}
                onClick={handleNavigateEmail}
              >
                <img
                  src={icons.headerIconEmail}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('headerNotificationIcon')}
                />
              </Button>
            </Tooltip>
          )}

          <Tooltip
            title={t('common_message_tile', { ns: 'common/base' })}
            placement='top'
            enterDelay={DEFAULT_TOOLTIP_DELAY}
            leaveDelay={DEFAULT_TOOLTIP_DELAY}
          >
            <Button
              variant='outlined'
              color='inherit'
              className={cx('headerNotification')}
              onClick={handleNavigateMessage}
            >
              <img
                src={icons.headerIconMessage}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('headerNotificationIcon')}
              />
            </Button>
          </Tooltip>

          <Tooltip
            title={t('common_notification_tile', { ns: 'common/base' })}
            placement='top'
            enterDelay={DEFAULT_TOOLTIP_DELAY}
            leaveDelay={DEFAULT_TOOLTIP_DELAY}
          >
            <Box component={'div'} className={cx('btnNotificationGroup')}>
              <Button
                variant='outlined'
                color='inherit'
                className={cx('headerNotification')}
                onClick={handleShowNotification}
              >
                <img
                  src={icons.headerIconNotification}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('headerNotificationIcon')}
                />
              </Button>

              {Number(notificationUnread) > DEFAULT_NUMBER_ZERO && (
                <Box component={'div'} className={cx('notificationQuantity')}>
                  <Box component={'div'} className={cx('quantity')}>
                    {notificationUnread}
                  </Box>
                </Box>
              )}
            </Box>
          </Tooltip>

          <DropdownProfile />
        </Stack>
      </Stack>

      <NotificationList
        isOpen={isOpenNotifications}
        anchorEl={anchorEl}
        onClose={handleCloseNotifications}
        onLoadMore={handleLoadMoreNotification}
        hasMore={hasMoreNotification}
      />

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </AppBar>
  );
};

export default Header;
